import React, { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useOptimizablePDLs } from '../../hooks/dashboard/useOptimizablePDLs';
import OptimizablePDLAccountGroup from './OptimizablePDLAccountGroup';
import EnedisDegradedBanner from './EnedisDegradedBanner';

const FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: '0',   label: '0% — À connecter' },
  { value: '33',  label: '33% — 1 fichier reçu' },
  { value: '66',  label: '66% — 2 fichiers reçus' },
  { value: '100', label: '100% — Prêt à optimiser' },
];

export default function OptimizablePDLTable() {
  const { data, degraded, warnings, loading, error, refetch } = useOptimizablePDLs();
  const [filterPct, setFilterPct] = useState('all');
  const [query, setQuery] = useState('');

  // Filtre + recherche au niveau PDL d'abord
  const filteredPdls = useMemo(() => {
    let rows = data;
    if (filterPct !== 'all') {
      const p = Number(filterPct);
      rows = rows.filter(r => r.progress_pct === p);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.company_name || '').toLowerCase().includes(q) ||
        (r.pdl_number || '').toLowerCase().includes(q) ||
        (r.site_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, filterPct, query]);

  // Groupement par compte
  const accounts = useMemo(() => {
    const map = new Map();
    for (const pdl of filteredPdls) {
      if (!map.has(pdl.account_id)) {
        map.set(pdl.account_id, {
          account_id: pdl.account_id,
          company_name: pdl.company_name,
          commercial_name: pdl.commercial_name,
          pdls: [],
        });
      }
      map.get(pdl.account_id).pdls.push(pdl);
    }
    // Tri : palier le plus haut (PDL ready en premier) puis nom alpha
    const list = Array.from(map.values());
    list.forEach(acc => {
      acc.pdls.sort((a, b) => b.progress_pct - a.progress_pct);
    });
    list.sort((a, b) => {
      const aMax = Math.max(...a.pdls.map(p => p.progress_pct));
      const bMax = Math.max(...b.pdls.map(p => p.progress_pct));
      if (aMax !== bMax) return bMax - aMax;
      return (a.company_name || '').localeCompare(b.company_name || '');
    });
    return list;
  }, [filteredPdls]);

  const isFiltering = filterPct !== 'all' || query.trim().length > 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* En-tête */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
            PDL connectables
          </h2>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {accounts.length} compte{accounts.length !== 1 ? 's' : ''} · {filteredPdls.length} PDL
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <label className="sr-only" htmlFor="optim-pdl-filter">État</label>
          <select
            id="optim-pdl-filter"
            value={filterPct}
            onChange={(e) => setFilterPct(e.target.value)}
            className="py-0.5 px-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-teal-300"
          >
            {FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <div className="relative">
            <FiSearch className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="pl-5 pr-2 py-1 border border-gray-200 rounded text-[10px] w-32 focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
        </div>
      </div>

      {!loading && !error && (
        <EnedisDegradedBanner degraded={degraded} warnings={warnings} onRetry={refetch} className="border-x-0 border-t-0" />
      )}
      {loading && <p className="px-3 py-4 text-center text-[10px] text-gray-500">Chargement…</p>}
      {error && (
        <div className="px-3 py-4 text-center text-[10px] text-red-600">
          Erreur de chargement. <button onClick={refetch} className="underline">Réessayer</button>
        </div>
      )}
      {!loading && !error && accounts.length === 0 && (
        <p className="px-3 py-4 text-center text-[10px] text-gray-500">Aucun compte connectable pour ces critères.</p>
      )}
      {!loading && !error && accounts.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 w-6"></th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Client / Site</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">PDL</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Commercial</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Connecté le</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Conso ann.</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">PS</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">État</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">Prêts</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acc => (
                <OptimizablePDLAccountGroup
                  key={acc.account_id}
                  account={acc}
                  defaultExpanded={isFiltering}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
